// The table footer: the range text on the left, the page-size select and the page links on the right.
// Rendered by DataTable; takes plain values only (DataTable is not compiled by the React Compiler, this is).
import type * as React from 'react';
import { useId } from 'react';

import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/components/lumen/pagination';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/lumen/select';
import { useLanguage } from '@/contexts/LanguageContext';
import { formatTemplate } from '@/lib/format-template';
import { cn } from '@/lib/utils';
import { getPageItems } from './table-logic';

const disabledLink = 'pointer-events-none opacity-50';

export interface DataTablePaginationProps {
  page: number;
  pageCount: number;
  onPageChange?: (page: number) => void;
  pageSize: number;
  pageSizeOptions: number[];
  /** Without it there is no page-size select. */
  onPageSizeChange?: (pageSize: number) => void;
  /** "1 - 10 / 42", or the loading / no-records text. */
  rangeText: React.ReactNode;
  className?: string;
}

export function DataTablePagination({
  page,
  pageCount,
  onPageChange,
  pageSize,
  pageSizeOptions,
  onPageSizeChange,
  rangeText,
  className,
}: DataTablePaginationProps) {
  const { t } = useLanguage();
  const sizeLabelId = useId();

  const go = (target: number) => (event: React.MouseEvent<HTMLAnchorElement>) => {
    // The links are anchors (Lumen PaginationLink) so they stay links for assistive tech; the page lives in
    // React state, not the URL.
    event.preventDefault();
    if (target < 1 || target > pageCount || target === page) return;
    onPageChange?.(target);
  };
  const atStart = page <= 1;
  const atEnd = page >= pageCount;

  return (
    <div
      data-slot="data-table-pagination"
      className={cn(
        'flex flex-wrap items-center justify-between gap-x-4 gap-y-2 border-line border-t px-4 py-2 text-13',
        className
      )}
    >
      <div className="min-w-0 text-fg-muted">{rangeText}</div>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        {onPageSizeChange && (
          <div className="flex items-center gap-2">
            <span id={sizeLabelId} className="text-fg-muted">
              {t('lumen.table.pageSize')}
            </span>
            <Select value={String(pageSize)} onValueChange={(value) => onPageSizeChange(Number(value))}>
              <SelectTrigger
                size="sm"
                aria-labelledby={sizeLabelId}
                className="h-8 w-auto min-w-[4.5rem] font-mono text-xs"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {pageSizeOptions.map((size) => (
                  <SelectItem key={size} value={String(size)} className="font-mono text-xs">
                    {size}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
        {pageCount > 0 && (
          <Pagination className="mx-0 w-auto">
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious
                  href="#"
                  onClick={go(page - 1)}
                  aria-disabled={atStart || undefined}
                  tabIndex={atStart ? -1 : undefined}
                  className={cn('h-8', atStart && disabledLink)}
                />
              </PaginationItem>
              {getPageItems(page, pageCount).map((item) =>
                typeof item === 'number' ? (
                  <PaginationItem key={item}>
                    <PaginationLink
                      href="#"
                      size="icon-sm"
                      isActive={item === page}
                      onClick={go(item)}
                      aria-label={formatTemplate(t('lumen.table.pageLink'), { n: item })}
                      className="font-mono text-xs"
                    >
                      {item}
                    </PaginationLink>
                  </PaginationItem>
                ) : (
                  <PaginationItem key={item}>
                    <PaginationEllipsis className="size-8" />
                  </PaginationItem>
                )
              )}
              <PaginationItem>
                <PaginationNext
                  href="#"
                  onClick={go(page + 1)}
                  aria-disabled={atEnd || undefined}
                  tabIndex={atEnd ? -1 : undefined}
                  className={cn('h-8', atEnd && disabledLink)}
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        )}
      </div>
    </div>
  );
}
